import { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastData {
  id: string;
  message: string;
  type: ToastType;
  duration?: number; // ms, default 4000
}

interface ToastProps extends ToastData {
  onDismiss: (id: string) => void;
}

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'bg-emerald-600 text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-[var(--color-primary)] text-white',
  warning: 'bg-amber-500 text-white',
};

const TYPE_ICONS: Record<ToastType, string> = {
  success: '\u2713',
  error: '\u2715',
  info: '\u2139',
  warning: '\u26A0',
};

/**
 * Individual toast notification with auto-dismiss and slide-in animation.
 * Renders as a small pill with icon, message, and close button.
 */
export function Toast({ id, message, type, duration = 4000, onDismiss }: ToastProps) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  // Trigger entrance animation on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Auto-dismiss after duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
    }, duration);
    return () => clearTimeout(timer);
  }, [duration]);

  // Remove from DOM after exit animation completes
  useEffect(() => {
    if (!exiting) return;
    const timer = setTimeout(() => {
      onDismiss(id);
    }, 300); // matches transition duration
    return () => clearTimeout(timer);
  }, [exiting, id, onDismiss]);

  const handleDismiss = () => {
    setExiting(true);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`
        pointer-events-auto flex items-center gap-2 px-4 py-3
        rounded-lg shadow-lg min-w-[280px] max-w-[400px]
        ${TYPE_STYLES[type]}
        transition-all duration-300 ease-out
        ${exiting ? 'opacity-0 translate-x-4' : visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
      `}
    >
      <span className="text-base flex-shrink-0" aria-hidden="true">
        {TYPE_ICONS[type]}
      </span>
      <span className="text-sm font-medium flex-1">{message}</span>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-0.5 rounded hover:bg-white/20 transition-colors"
        aria-label="Dismiss notification"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

Toast.displayName = 'Toast';

export default Toast;
